import { useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export const ForgotPasswordDialog = ({ trigger }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // {already_open: bool}

  const reset = () => { setEmail(""); setNote(""); setDone(null); };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/password-tickets", { email, note });
      setDone({ already_open: !!r.data.already_open });
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><LifeBuoy className="h-4 w-4" />Forgot password</DialogTitle>
          <DialogDescription>Raise a quick ticket. Admin will set a new default password and share it with you.</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-sm text-emerald-900" data-testid="forgot-password-success">
            <div className="flex items-center gap-2 font-medium"><CheckCircle2 className="h-4 w-4" />
              {done.already_open ? "You already have an open ticket." : "Ticket raised."}
            </div>
            <p className="text-emerald-800 mt-2 leading-relaxed">
              Admin will reach out shortly with a new default password you can use to sign in and then change from your dashboard.
            </p>
            <DialogFooter className="mt-4">
              <Button onClick={() => setOpen(false)} data-testid="forgot-password-close" className="bg-emerald-700 hover:bg-emerald-800 text-white">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Account email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" data-testid="forgot-password-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Message to admin (optional)</Label>
              <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                maxLength={400} placeholder="e.g. I forgot my password after signing up last week."
                data-testid="forgot-password-note" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={submitting} data-testid="forgot-password-submit"
                className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white">
                {submitting ? "Raising…" : "Raise ticket"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
